<template>
  <!-- 0. 소셜 로그인 선택 -->
  <div v-if="step == 0" class="pt-5 lg:pt-8">
    <div v-if="lazy" class="h-full w-full pt-[20vh]">
      <LoadingSpinner />
    </div>
    <div v-else class="inline-flex h-full w-full flex-col items-center justify-center">
      <picture>
        <source
          srcset="
            assets/image/img_login/img_login.webp      1x,
            assets/image/img_login/img_login@1.5x.webp 1.5x,
            assets/image/img_login/img_login@2x.webp   2x,
            assets/image/img_login/img_login@3x.webp   3x
          "
          type="image/webp"
        />
        <img src="assets/image/img_login/img_login.png" alt="가디와 앤 캐릭터" />
      </picture>
      <div class="text-cneter inline-flex flex-col items-center gap-2.5 pb-7 pt-5">
        <slot />
      </div>
      <div
        class="item-center inline-flex w-full flex-col items-center gap-5 border-t border-dashed border-slate-400 pt-7"
      >
        <Text size="text-base" bold leading="none" color="gray">{{ $t('login.coment.sns') }}</Text>
        <ul class="flex gap-3" data-testid="login_list">
          <li v-for="item in btnIcons" :key="item.name" class="h-11 w-11">
            <button :data-testid="`login-${item.name}`" @click="socialRequest(item.name)">
              <img :src="item.icon" :alt="`${item.name} login button`" />
            </button>
          </li>
          <button data-testid="test_login" @click="test_login" />
        </ul>
      </div>
    </div>
  </div>
  <!-- 1. 프로필 사진 선택 -->
  <div v-else-if="step == 1" class="flex w-full flex-col items-center pt-14" data-testid="step-1">
    <strong class="text-[22px] text-xl font-semibold text-zinc-800">
      {{ $t('login.step1.title') }}
    </strong>
    <div class="my-5 py-3.5">
      <div class="left-0 top-0 h-36 w-36 rounded-full bg-white">
        <img
          v-if="userInfo.profile_image == null"
          src="assets/svg/profile_default.svg"
          alt=""
          class="h-36 w-36 rounded-full"
        />
        <img v-else :src="profileArr[userInfo.profile_image]" class="h-36 w-36 rounded-full" />
      </div>
    </div>
    <ul class="grid w-80 grid-cols-5 gap-3" data-testid="profile-list">
      <li v-for="value in profiles" :key="value.name" class="relative h-14 w-14">
        <div
          :class="[
            'absolute left-0 top-0 h-14 w-14 cursor-pointer rounded-3xl bg-white',
            {'border-2 border-blue-600': value.name == userInfo.profile_image}
          ]"
          @click="setProfileImage(value.name)"
        >
          <img class="absolute h-14 w-14" :src="value.img" :alt="value.name" />
        </div>
      </li>
    </ul>
    <div class="w-full max-w-96 pt-10">
      <Button
        :disabled="userInfo.profile_image == null"
        size="xl"
        class="p-0"
        color="blue"
        data-testid="btn-next"
        @click-event="step = 2"
      >
        {{ $t('login.button.next') }}
      </Button>
    </div>
  </div>
  <!-- 2. 부모 자녀 선택 -->
  <div v-else-if="step == 2" class="flex w-full flex-col items-center px-5 pt-14" data-testid="step-2">
    <strong class="text-[22px] text-xl font-semibold text-zinc-800">
      {{ $t('login.step2.title') }}
    </strong>
    <picture>
      <source
        srcset="
          assets/image/pq_family/img_pq_family.webp      1x,
          assets/image/pq_family/img_pq_family@1.5x.webp 1.5x,
          assets/image/pq_family/img_pq_family@2x.webp   2x,
          assets/image/pq_family/img_pq_family@3x.webp   3x
        "
        type="image/webp"
      />
      <img src="assets/image/pq_family/img_pq_family.png" class="w-48 pb-10 pt-[30px]" alt="부모 또는 자녀" />
    </picture>
    <div class="flex w-full justify-center gap-2">
      <Button class="w-44 sm:min-w-[180px]" outline="primary" size="lg" @click-event="setType('parent')">
        {{ $t('login.step2.type.parents') }}
      </Button>
      <Button
        class="w-44 sm:min-w-[180px]"
        outline="primary"
        size="lg"
        data-testid="btn-child"
        @click-event="setType('child')"
      >
        {{ $t('login.step2.type.child') }}
      </Button>
    </div>
  </div>
  <!-- 3. 자녀를 선택했을 경우 조수 선택 -->
  <div v-else-if="step == 3" class="flex w-full flex-col items-center px-5 pt-14" data-testid="step-3">
    <strong class="text-[22px] text-xl font-semibold text-zinc-800">
      {{ $t('login.step3.title') }}
    </strong>
    <picture>
      <source
        srcset="
          assets/image/guardi_ann/img_guardi_ann.webp      1x,
          assets/image/guardi_ann/img_guardi_ann@1.5x.webp 1.5x,
          assets/image/guardi_ann/img_guardi_ann@2x.webp   2x,
          assets/image/guardi_ann/img_guardi_ann@3x.webp   3x
        "
        type="image/webp"
      />
      <img src="assets/image/guardi_ann/img_guardi_ann.png" class="w-48 pb-10 pt-[30px]" alt="가디 또는 앤" />
    </picture>
    <div class="flex w-full justify-center gap-2">
      <Button class="w-44 sm:min-w-[180px]" outline="primary" size="lg" @click-event="setAssistant('guardi')">
        {{ $t('login.step3.type', 1) }}
      </Button>
      <Button
        class="w-44 sm:min-w-[180px]"
        outline="primary"
        size="lg"
        data-testid="btn-ann"
        @click-event="setAssistant('ann')"
      >
        {{ $t('login.step3.type', 0) }}
      </Button>
    </div>
  </div>
  <!-- 4. 회원가입 완료 -->
  <div v-else class="inline-flex flex-col items-center justify-center pt-5 lg:pt-8" data-testid="step-finish">
    <picture>
      <source
        srcset="
          assets/image/pq_test/img_pq_test.webp      1x,
          assets/image/pq_test/img_pq_test@1.5x.webp 1.5x,
          assets/image/pq_test/img_pq_test@2x.webp   2x,
          assets/image/pq_test/img_pq_test@3x.webp   3x
        "
        type="image/webp"
      />
      <img src="assets/image/pq_test/img_pq_test.png" class="h-40" alt="가디 또는 앤" />
    </picture>
    <div class="text-cneter inline-flex flex-col items-center gap-2.5 pt-5">
      <span class="text-xl font-bold leading-loose">{{ $t('login.finsh.title') }}</span>
      <Text size="text-base" bold leading="normal">{{ $t('login.finsh.context') }}</Text>
    </div>
    <li class="flex w-full flex-col items-center pt-10">
      <Button
        class="w-full min-w-fit px-4 md:w-96"
        outline="blur"
        size="lg"
        data-testid="btn-next"
        @click-event="goNext()"
      >
        {{ $t('login.button.continue') }}
      </Button>
    </li>
  </div>
</template>

<script setup>
import googleIcon from '~/assets/svg/btn_login_google.svg'
// import appleIcon from '~/assets/svg/btn_login_apple.svg'
import kakaoIcon from '~/assets/svg/btn_login_kakao.svg'
import naverIcon from '~/assets/svg/btn_login_naver.svg'
import profiles, {profileArr} from '~/data/profileImages.js'
import Button from '~/components/common/Button.vue'
import Text from '~/components/common/Text.vue'
import LoadingSpinner from '~/components/LoadingSpinner.vue'
import {useGtag} from 'vue-gtag-next'
const localeRoute = useLocaleRoute()
const runtimeConfig = useRuntimeConfig()
const {$sentrySetUser} = useNuxtApp()
const authStore = useAuthStore()
const route = useRoute()
const source = authStore.userInfo.source
const userInfo = useState('userInfo', () => {})
const step = ref(route.query.step ?? 0) // 회원가입 단계
const code = route.query.code ?? undefined
const btnIcons = [
  {name: 'google', icon: googleIcon},
  // {name: 'apple', icon: appleIcon},
  {name: 'kakao', icon: kakaoIcon},
  {name: 'naver', icon: naverIcon}
]
const lazy = ref(false)
const {event} = useGtag()

onMounted(() => {
  if (code != undefined) {
    login()
  } else if (authStore.isLogin) {
    navigateTo(localeRoute('/'))
  }
})

const login = async () => {
  try {
    lazy.value = true
    await authApi.login(source, {code})

    // 사용자 정보 가져오기
    const userData = await profileApi.getUser()
    userInfo.value = {...userData, profile_image: null}

    // 회원가입 여부에 따른 처리
    if (userInfo.value.type == null || userInfo.value.type === undefined) {
      const lang = localStorage.getItem('lang') || 'ko'
      navigateTo({path: `/${lang}/auth/login`, query: {step: 1}})
    } else {
      goNext()
    }
  } finally {
    lazy.value = false
  }
}

const test_login = async () => {
  let lang = route.path.split('/')[1]
  lang = lang.includes('ko', 'en', 'jp') ? lang : 'ko'
  localStorage.setItem('path', route.path)
  localStorage.setItem('lang', lang)
  authStore.setUserSource('kakao')

  await authApi.e_login().then(res => {
    userInfo.value = {...res, profile_image: null}
    if (userInfo.value.type == null || userInfo.value.type == undefined) {
      navigateTo({path: `/${lang}/auth/login`, query: {step: 1}})
    } else {
      goNext()
    }
  })
}

const socialRequest = async source => {
  const oauthUrl = runtimeConfig.public[`${source}OauthUrl`]
  const clientId = runtimeConfig.public[`${source}ClientId`]
  const redirectUrl = `${window.location.origin}/auth/login`
  const states = 'guardiann'
  let lang = route.path.split('/')[1] // 현재 언어
  lang = ['ko', 'en', 'jp'].includes(lang) ? lang : 'ko'
  localStorage.setItem('path', route.path)
  localStorage.setItem('lang', lang)
  authStore.setUserSource(source)
  event('login', {
    category: '홈페이지 로그인',
    label: source
  })

  if (source === 'naver')
    window.location.href = `${oauthUrl}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUrl}&state=${states}`
  if (source === 'kakao')
    window.location.href = `${oauthUrl}?client_id=${clientId}&redirect_uri=${redirectUrl}&response_type=code&state=${states}`
  if (source === 'google')
    window.location.href = `${oauthUrl}?response_type=code&scope=email profile&client_id=${clientId}&redirect_uri=${redirectUrl}&state=${states}`
}

const setProfileImage = profile_image => {
  userInfo.value.profile_image = profile_image
}

const setType = type => {
  userInfo.value.type = type
  if (type === 'child') {
    step.value = 3 // 자녀일 경우 조수 선택
  } else {
    setUserDB() // 부모일 경우 회원가입 완료
  }
}

const setAssistant = assistant => {
  userInfo.value.assistant = assistant
  setUserDB() // 조수 선택 후 회원가입 완료
}

const requestData = () => {
  const data = {profile_image: userInfo.value.profile_image, type: userInfo.value.type}
  if (userInfo.value.type == 'child') {
    return {...data, assistant: userInfo.value.assistant}
  } else {
    return data
  }
}

const setUserDB = () => {
  profileApi.setUser(requestData()).then(() => {
    step.value = 4
  })
}

const goNext = async () => {
  authStore.setLogin()
  authStore.setUserInfo(userInfo.value)
  $sentrySetUser({id: userInfo.value.id, username: userInfo.value.name, email: userInfo.value.email})
  const nowPath = localStorage.getItem('path')
  const lang = localStorage.getItem('lang')
  localStorage.removeItem('path')
  localStorage.removeItem('lang')
  if (nowPath.includes('/auth/login')) navigateTo(`/${lang}`)
  else navigateTo(nowPath)
}
</script>

<style lang="scss" scoped></style>
